/**
 * Route helpers.
 * -----------------------------------------------------------------------------
 * Weglot can add the active language/market as the first URL segment while
 * Shopify's route helpers still return the base route (for example `/search`).
 */

const localePrefixPattern = /^[a-z]{2}(?:-[a-z]{2})?$/i
const absoluteRoutePattern = /^(?:[a-z][a-z0-9+.-]*:|\/\/)/i
const hashRoutePattern = /^#/

/**
 * Get the active language/market prefix from the current path.
 * @returns {String}
 */
function getCurrentLocalePrefix() {
  const [firstSegment = ''] = window.location.pathname.split('/').filter(Boolean)

  return localePrefixPattern.test(firstSegment)
    ? `/${firstSegment}`
    : ''
}

/**
 * Prefix a route with the active Weglot/Markets locale segment when Shopify's
 * route helper has returned the un-prefixed route.
 * @param {String} route - Route path.
 * @returns {String}
 */
export function getLocalisedRoute(route = '/') {
  if (!route || hashRoutePattern.test(route)) {
    return route
  }

  let path = route.startsWith('/') ? route : `/${route}`

  if (absoluteRoutePattern.test(route)) {
    const url = new URL(route, window.location.origin)

    if (url.origin !== window.location.origin) {
      return route
    }

    path = `${url.pathname}${url.search}${url.hash}`
  }

  const localePrefix = getCurrentLocalePrefix()

  if (!localePrefix || path === localePrefix || path.startsWith(`${localePrefix}/`)) {
    return path
  }

  if (path === '/') {
    return `${localePrefix}/`
  }

  return `${localePrefix}${path}`
}

/**
 * Build a locale-aware search URL.
 * @param {String} [query] - Optional search query.
 * @returns {String}
 */
export function getSearchUrl(query = '') {
  const searchRoute = window.cnvs?.routes?.search || '/search'
  const url = new URL(getLocalisedRoute(searchRoute), window.location.origin)

  if (query) {
    url.searchParams.set('query', query)
  }

  return `${url.pathname}${url.search}`
}
